import { useId, type ReactNode, type Ref, type TextareaHTMLAttributes } from "react";
import { cn } from "../lib/cn";
import { FieldLabel } from "./FieldLabel";
import { useFormSkin } from "./FormSkinContext";
import { XIcon } from "./icons";

/** Multiline counterpart of Input's FIELD_SHELL_SKIN. It sizes by padding, never a fixed height. */
const TEXTAREA_SHELL_SKIN = {
  styleguide: "rounded-2xl px-4 py-3.5 text-[17px] leading-[1.5]",
  enroll: "rounded-[20px] px-5 py-4 text-[15px] leading-[1.5]",
} as const;

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  /** Show the required-asterisk in the label. Decoupled from the native `required` attribute. */
  required?: boolean | undefined;
  /** Optional content rendered to the right of the label text. */
  labelAddon?: ReactNode | undefined;
  /** React 19 accepts `ref` as a regular prop on function components, so no forwardRef is needed. */
  ref?: Ref<HTMLTextAreaElement> | undefined;
}

/**
 * Multiline counterpart of `Input`, with the same label, hint, error, required
 * and labelAddon contract via `FieldLabel`, and `ref` support.
 *
 * Pair it with react-hook-form via `<Textarea {...register("foo")} />`.
 */
export function Textarea({
  ref,
  label,
  hint,
  error,
  required,
  labelAddon,
  className,
  id,
  rows = 4,
  ...rest
}: TextareaProps): ReactNode {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const errorId = error ? `${textareaId}-error` : undefined;
  const shell = TEXTAREA_SHELL_SKIN[useFormSkin()];
  return (
    <div>
      <FieldLabel htmlFor={textareaId} required={required} hint={hint} addon={labelAddon}>
        {label}
      </FieldLabel>
      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        {...rest}
        className={cn(
          "border-border bg-input-bg text-foreground block w-full border-2",
          shell,
          "placeholder:text-placeholder",
          "focus:border-primary focus:bg-white focus:outline-none",
          "disabled:text-cloud-blue disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-danger/40 bg-danger-bg focus:border-danger",
          className,
        )}
      />
      {error && (
        <p
          id={errorId}
          role="alert"
          className="text-danger mt-1.5 flex items-center gap-1.5 text-[13px] leading-[1.5]"
        >
          <XIcon size={13} className="shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}
